import esriConfig from "@arcgis/core/config.js";
import Map from "@arcgis/core/Map.js";
import WebMap from "@arcgis/core/WebMap.js";
import Portal from "@arcgis/core/portal/Portal.js";
import PortalItem from "@arcgis/core/portal/PortalItem.js";
import Point from "@arcgis/core/geometry/Point.js";
import * as locator from "@arcgis/core/rest/locator.js";

export const GEOCODE_URL =
  "https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer";

export const MAP_LOAD_MS = 18_000;
export const GEOCODE_MS = 22_000;
export const VIEW_LOAD_MS = 15_000;

/** CVIN org portal — default when REACT_APP_ARCGIS_PORTAL_URL is unset at build time. */
export const DEFAULT_ARCGIS_PORTAL_URL = "https://cvinet.maps.arcgis.com";

export function getArcgisApiKey() {
  return (process.env.REACT_APP_ARCGIS_API_KEY || "").trim();
}

export function getArcgisPortalUrl() {
  const fromEnv = (process.env.REACT_APP_ARCGIS_PORTAL_URL || "").trim();
  return (fromEnv || DEFAULT_ARCGIS_PORTAL_URL).replace(/\/$/, "");
}

export function getArcgisSegmentsKmzUrl(apiBase) {
  const fromEnv = (process.env.REACT_APP_ARCGIS_SEGMENTS_KMZ_URL || "").trim();
  if (fromEnv) return fromEnv;
  const base = apiBase != null ? String(apiBase).trim().replace(/\/$/, "") : "";
  if (base) return `${base}/map-assets/segments.kmz`;
  return "";
}

export function configureArcgisApiKey(apiKey) {
  const key = (apiKey || getArcgisApiKey() || "").trim();
  if (key) esriConfig.apiKey = key;
  esriConfig.portalUrl = getArcgisPortalUrl();
}

function arcgisErrorMessage(err) {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err.message) return err.message;
  if (Array.isArray(err.details) && err.details[0]?.message) return err.details[0].message;
  return String(err);
}

function webMapPortalCandidates() {
  const configured = getArcgisPortalUrl();
  const defaults = [DEFAULT_ARCGIS_PORTAL_URL, "https://www.arcgis.com"];
  const out = [];
  for (const url of [configured, ...defaults]) {
    const normalized = String(url || "").trim().replace(/\/$/, "");
    if (normalized && !out.includes(normalized)) out.push(normalized);
  }
  return out;
}

function webMapLoadFailureError(err, webmapId, portalUrlsTried) {
  const detail = arcgisErrorMessage(err);
  const portals = portalUrlsTried.join(", ");
  let msg = detail
    ? `Could not load ArcGIS web map ${webmapId}: ${detail}`
    : `Could not load the configured ArcGIS web map (REACT_APP_ARCGIS_WEBMAP_ID=${webmapId}).`;
  msg += ` Portal(s) tried: ${portals}.`;
  msg += " Set REACT_APP_ARCGIS_PORTAL_URL to your org portal (e.g. https://yourorg.maps.arcgis.com) if the map is not on ArcGIS Online.";
  msg += " The API key must belong to that org and be allowed to access this web map and its layers.";
  if (typeof window !== "undefined" && window.location?.origin) {
    msg += ` For npm run dev, allow ${window.location.origin} in the API key HTTP referrer list.`;
  }
  return msg;
}

async function loadWebMapFromPortal(apiKey, webmapId, portalUrl) {
  const key = (apiKey || "").trim();
  esriConfig.portalUrl = portalUrl;
  if (key) esriConfig.apiKey = key;

  const portal = new Portal({ url: portalUrl });
  if (key) portal.apiKey = key;
  await withTimeout(loadResource(portal), MAP_LOAD_MS, "ArcGIS portal");

  const portalItem = new PortalItem({
    id: webmapId,
    portal,
  });
  if (key) portalItem.apiKey = key;
  await withTimeout(loadResource(portalItem), MAP_LOAD_MS, "ArcGIS portal item");

  const webMap = new WebMap({ portalItem });
  await withTimeout(loadResource(webMap), MAP_LOAD_MS, "ArcGIS web map");
  return webMap;
}

/** Load a web map by portal item id (tries configured portal, then ArcGIS Online). */
export async function loadWebMapById(apiKey, webmapId) {
  const id = String(webmapId || "").trim();
  if (!id) throw new Error("Web map id is required.");
  configureArcgisApiKey(apiKey);
  const portalCandidates = webMapPortalCandidates();
  let lastError = null;
  for (const portalUrl of portalCandidates) {
    try {
      return await loadWebMapFromPortal(apiKey, id, portalUrl);
    } catch (err) {
      lastError = err;
    }
  }
  throw new Error(webMapLoadFailureError(lastError, id, portalCandidates));
}

export function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`));
    }, ms);
    Promise.resolve(promise)
      .then((v) => {
        clearTimeout(timer);
        resolve(v);
      })
      .catch((e) => {
        clearTimeout(timer);
        reject(e);
      });
  });
}

/** ArcGIS Loadable: prefer load(), fall back to when(). */
export function loadResource(resource) {
  if (!resource) return Promise.reject(new Error("Nothing to load"));
  if (typeof resource.load === "function") return resource.load();
  if (typeof resource.when === "function") return resource.when();
  return Promise.resolve(resource);
}

/** Only treat as lat/lng when both comma-separated parts are plain numbers. */
export function parseLatLngPair(text) {
  const s = String(text).trim();
  const parts = s.split(",").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const re = /^-?\d+(\.\d+)?$/;
  if (!re.test(parts[0]) || !re.test(parts[1])) return null;
  const a = parseFloat(parts[0]);
  const b = parseFloat(parts[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) {
    return { latitude: a, longitude: b };
  }
  if (Math.abs(b) <= 90 && Math.abs(a) <= 180) {
    return { latitude: b, longitude: a };
  }
  return { latitude: a, longitude: b };
}

export async function loadMapForPreview(apiKey, options = {}) {
  const { requireWebMap = false, webmapId: webmapIdOverride } = options;
  const webmapId = String(webmapIdOverride || process.env.REACT_APP_ARCGIS_WEBMAP_ID || "").trim();
  const portalCandidates = webMapPortalCandidates();

  configureArcgisApiKey(apiKey);

  if (webmapId) {
    let lastError = null;
    for (const portalUrl of portalCandidates) {
      try {
        return await loadWebMapFromPortal(apiKey, webmapId, portalUrl);
      } catch (err) {
        lastError = err;
      }
    }
    if (requireWebMap) {
      throw new Error(webMapLoadFailureError(lastError, webmapId, portalCandidates));
    }
    /* API key may lack access to this portal item — fall back to default basemap */
  }

  if (requireWebMap) {
    throw new Error(
      "REACT_APP_ARCGIS_WEBMAP_ID is not set. Use the same web map id as project landing pages to show the fiber network."
    );
  }

  const map = new Map({ basemap: "arcgis-streets" });
  await withTimeout(loadResource(map), MAP_LOAD_MS, "ArcGIS basemap");
  return map;
}

/** Convert #rrggbb to ArcGIS [r,g,b,a] for marker symbols. */
export function hexToEsriRgba(hex, alpha = 1) {
  const h = String(hex || "")
    .replace(/^#/, "")
    .trim();
  if (h.length !== 6) return [100, 116, 139, Math.round(alpha * 255)];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [r, g, b, Math.round(alpha * 255)];
}

export async function resolveServiceLocationPoint(loc, isCancelled, apiKey) {
  const pair = parseLatLngPair(loc);
  if (pair) {
    return new Point({ longitude: pair.longitude, latitude: pair.latitude });
  }
  const results = await locator.addressToLocations(GEOCODE_URL, {
    address: { SingleLine: loc },
    maxLocations: 1,
    apiKey: apiKey || undefined,
  });
  if (isCancelled()) return null;
  const hit = results?.[0]?.location;
  if (!hit) return null;
  return new Point({
    longitude: hit.longitude,
    latitude: hit.latitude,
    spatialReference: hit.spatialReference,
  });
}
